import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { BasicModal, Button, DropdownButton, TabbedDropdown } from "@similarweb/ui-components";

import { DropdownSmartTabs } from "app/pages/sales-intelligence/common-components/dropdown/DropdownTabs/DropdownTabs";
import DropdownTabPanel from "app/pages/sales-intelligence/common-components/form-fields/DropdownTabPanel/DropdownTabPanel";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import type { IAppsSdkDescriptionItemDto } from "app/pages/sales-intelligence/domain/sdk/types/dto";
import useDropdownTabbedSearch from "app/pages/sales-intelligence/modules/technology/hook/useDropdownTabbedSearch";
import {
    type OperatorOption,
    type InclusionOption,
    inclusionOptionMap,
    operatorOptionMap,
} from "app/pages/sales-intelligence/types/commonMaps";

import type { IAppsSDKsFilterItem } from "../../../types/commonFiltersDto";
import type { IAppsSDKsFilterDescriptionDto } from "../../../types/descriptionDto";
import type { DisabledDropdownSdkValues } from "../../../types/dropdown";
import {
    getAllCategoriesFromSdkDto,
    getDisabledSdkValues,
    getSdkCategoryDDItems,
    getSdksDDItem,
    hasSameSdkObject,
} from "../../../utils";
import InclusionRadioButtons from "../../InclusionRadioButtons/InclusionRadioButtons";
import OperatorsDropdown from "../../OperatorsDropdown/OperatorsDropdown";
import SdkChipsGroup from "../../SdkChipsGroup/SdkChipsGroup";
import { filterModalStyles, getFilterModalCloseIconConfig } from "../configs";
import {
    StyledButtonsContainer,
    StyledContainerWithChips,
    StyledDDContent,
    StyledHeader,
    StyledMainContainer,
    StyledModalContent,
    StyledOperationContainer,
    StyledSubtitle,
    StyledTitle,
} from "../styles";

import { StyledDropdownButtonContainer, StyledTabbedDropdownWrapper } from "./styles";

const INIT_SDK_VALUE: IAppsSDKsFilterItem = {
    inclusion: inclusionOptionMap.includeOnly,
    operator: operatorOptionMap.and,
    categories: [],
    sdks: [],
};

type SdkModalProps = {
    isOpen: boolean;
    value: readonly IAppsSDKsFilterItem[] | null | undefined;
    selected: IAppsSDKsFilterItem | null;
    options: IAppsSDKsFilterDescriptionDto;
    onCancel: VoidFunction;
    onClose: VoidFunction;
    onApply: (item: IAppsSDKsFilterItem, selected?: IAppsSDKsFilterItem | null) => void;
};

const SdkModal: FC<SdkModalProps> = ({ isOpen, onClose, selected, onCancel, onApply, options, value }) => {
    const { translate } = useTranslate();

    const [isOpenDD, setIsOpenDD] = useState(false);

    const [sdkItem, setSdkItem] = useState<IAppsSDKsFilterItem>(INIT_SDK_VALUE);

    const hasChips = sdkItem.sdks.length > 0 || sdkItem.categories.length > 0;

    const dropdownTitle = translate("si.find_apps.filters.sdk.dropdown.title");

    const initValue = () => {
        setSdkItem(INIT_SDK_VALUE);
    };

    const handleSetInclusion = (inclusionOption: InclusionOption) => {
        const sdk = { ...sdkItem, inclusion: inclusionOption };
        if (inclusionOption === inclusionOptionMap.excludeOnly) {
            sdk.operator = operatorOptionMap.or;
        }
        setSdkItem(sdk);
    };

    const handleCancel = () => {
        onCancel();
        initValue();
    };

    const handleOnApply = () => {
        onApply(sdkItem, selected);
        initValue();
    };

    const isApplyDisabled = () => {
        return (sdkItem.categories.length === 0 && sdkItem.sdks.length === 0) || hasSameSdkObject(sdkItem, selected);
    };

    const handleOnToggleClick = (open: boolean) => {
        setIsOpenDD(open);
    };

    const handleOnItemClick = (item: { name: string; secondaryName?: string }) => {
        const { categories, sdks } = sdkItem;
        let updatedCategories: string[] = [];
        if (!item.secondaryName) {
            if (categories.includes(item.name)) {
                updatedCategories = categories.filter((category: string) => category !== item.name);
            } else {
                updatedCategories = [...categories, item.name];
            }

            setSdkItem((prev) => ({ ...prev, categories: updatedCategories }));
        } else {
            const updatedSdks = sdks.includes(item.name)
                ? sdks.filter((sdk: string) => sdk !== item.name)
                : [...sdks, item.name];

            setSdkItem((prev) => ({ ...prev, sdks: updatedSdks }));
        }
    };

    const handleChangeOperator = (value: OperatorOption) => {
        setSdkItem((prev) => ({ ...prev, operator: value }));
    };

    useEffect(() => {
        if (isOpen && selected) {
            setSdkItem(selected);
        }

        if (!isOpen) {
            initValue();
        }
    }, [isOpen, selected]);

    const isEnabledExclude = useMemo((): boolean => {
        return (
            (selected && selected?.inclusion === inclusionOptionMap.excludeOnly) ||
            !value?.find((item) => item.inclusion === inclusionOptionMap.excludeOnly)
        );
    }, [selected, value]);

    const tabs = useMemo(() => {
        const disabledValues: DisabledDropdownSdkValues = getDisabledSdkValues(value ?? [], selected);

        const categories: string[] = Array.from(new Set(getAllCategoriesFromSdkDto(options.sdksDescriptions)));

        const sdkTab = {
            id: "sdk-names",
            name: translate("si.find_apps.filters.sdks.tabs.sdk-names", {
                amountOfItems: options.sdksDescriptions.length,
            }),
            items: options.sdksDescriptions.map((item: IAppsSdkDescriptionItemDto) => {
                return getSdkCategoryDDItems(item, disabledValues, sdkItem);
            }),
        };

        const categoryTab = {
            id: "category-tab",
            name: translate("si.find_apps.filters.sdks.tabs.category-names", {
                amountOfItems: categories.length,
            }),
            items: categories.map((item: string) => {
                return getSdksDDItem(item, disabledValues, sdkItem);
            }),
        };

        return [categoryTab, sdkTab];
    }, [options, sdkItem, value]);

    const { tabsWithSortedItems, searchTerm, searchProps } = useDropdownTabbedSearch(
        {
            tabs,
            searchPlaceholder: translate("si.find_apps.filters.sdk.dropdown.placeholder"),
        },
        { shouldProcessItems: isOpen },
    );

    return (
        <BasicModal
            isOpen={isOpen}
            onRequestClose={onClose}
            customStyles={filterModalStyles}
            closeIconConfig={getFilterModalCloseIconConfig(onClose)}
            dataAutomation="sdk-filter-modal"
        >
            <div>
                <StyledHeader>
                    <I18n
                        translatable={{ key: "si.find_apps.filters.sdks.modal.title" }}
                        element={StyledTitle}
                    />
                    <I18n
                        translatable={{ key: "si.find_apps.filters.sdks.modal.subtitle" }}
                        element={StyledSubtitle}
                    />
                </StyledHeader>
                <StyledModalContent>
                    <StyledMainContainer>
                        <InclusionRadioButtons
                            isExcludeAvailable={isEnabledExclude}
                            selectedInclusion={sdkItem.inclusion}
                            onSelect={handleSetInclusion}
                        />
                        <StyledContainerWithChips hasChips={hasChips}>
                            <SdkChipsGroup
                                sdk={sdkItem}
                                onChangeSdk={setSdkItem}
                            />
                            <StyledDDContent>
                                {sdkItem.inclusion === inclusionOptionMap.includeOnly ? (
                                    <StyledOperationContainer>
                                        <OperatorsDropdown
                                            options={options.operatorOptions}
                                            value={sdkItem.operator}
                                            onChange={handleChangeOperator}
                                        />
                                    </StyledOperationContainer>
                                ) : null}

                                <StyledTabbedDropdownWrapper>
                                    <TabbedDropdown
                                        dropdownTitle={dropdownTitle}
                                        onItemClick={handleOnItemClick}
                                        isOpen={isOpenDD}
                                        searchTerm={searchTerm}
                                        tabs={tabsWithSortedItems}
                                        buttonComponent={() => (
                                            <StyledDropdownButtonContainer>
                                                <DropdownButton>{dropdownTitle}</DropdownButton>
                                            </StyledDropdownButtonContainer>
                                        )}
                                        panelComponent={DropdownTabPanel}
                                        tabsView={DropdownSmartTabs}
                                        onToggle={handleOnToggleClick}
                                        searchProps={searchProps}
                                        className="sdk-category-dd"
                                        height={240}
                                        fullWidth
                                    />
                                </StyledTabbedDropdownWrapper>
                            </StyledDDContent>
                        </StyledContainerWithChips>
                    </StyledMainContainer>
                    <StyledButtonsContainer>
                        <Button
                            type="flat"
                            onClick={handleCancel}
                            dataAutomation="sdk-modal-button-cancel"
                        >
                            {translate("si.common.button.cancel")}
                        </Button>
                        <Button
                            onClick={handleOnApply}
                            isDisabled={isApplyDisabled()}
                            dataAutomation="sdk-modal-button-apply"
                        >
                            {translate(`common.apply`)}
                        </Button>
                    </StyledButtonsContainer>
                </StyledModalContent>
            </div>
        </BasicModal>
    );
};

export default SdkModal;
